// src/redux/actions/credibilityActionThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import CredibilityAPI from "./credibilityAPI";

/** Get current credibility (handles 200 + 202 soft placeholder) */
export const fetchCredibilityThunk = createAsyncThunk(
  "credibility/fetchUser",
  async ({ userId, soft = true }, { rejectWithValue }) => {
    try {
      const { data, status } = await CredibilityAPI.fetchUserCredibility(userId, { soft });
      return { userId, data, status };
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch credibility");
    }
  }
);

/** Get user history snapshots */
export const fetchCredHistoryThunk = createAsyncThunk(
  "credibility/fetchHistory",
  async ({ userId, limit = 50 }, { rejectWithValue }) => {
    try {
      const history = await CredibilityAPI.fetchUserHistory(userId, { limit });
      return { userId, history };
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch credibility history");
    }
  }
);

/** Get leaderboard */
export const fetchCredLeaderboardThunk = createAsyncThunk(
  "credibility/fetchLeaderboard",
  async ({ limit = 50 } = {}, { rejectWithValue }) => {
    try {
      const list = await CredibilityAPI.fetchLeaderboard({ limit });
      return { list };
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch credibility leaderboard");
    }
  }
);

/** Enqueue async recompute → usually followed by polling fetchCredibilityThunk(soft:true) */
export const enqueueCredRecomputeThunk = createAsyncThunk(
  "credibility/enqueueRecompute",
  async ({ userId }, thunkAPI) => {
    try {
      const receipt = await CredibilityAPI.enqueueRecompute(userId);
      // Immediately soft-fetch to reflect pending state if it flips to 202
      thunkAPI.dispatch(fetchCredibilityThunk({ userId, soft: true }));
      return { userId, receipt };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message || "Failed to enqueue credibility recompute");
    }
  }
);

/** Admin-only sync recompute (immediate result) */
export const recomputeCredSyncThunk = createAsyncThunk(
  "credibility/recomputeSync",
  async ({ userId }, { rejectWithValue }) => {
    try {
      const data = await CredibilityAPI.recomputeSync(userId);
      return { userId, data };
    } catch (err) {
      return rejectWithValue(err.message || "Failed to recompute credibility (sync)");
    }
  }
);

export const fetchGroupCredibility = createAsyncThunk(
  "credibility/fetchGroupCredibility",
  async (groupId, { rejectWithValue }) => {
    try {
      return await CredibilityAPI.fetchGroupCredibility(groupId);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);